/**
 * +castux: gameplay settings (casting feel), persisted in localStorage like the combat text
 * settings; every storage access is in try/catch.
 *  - `quickCastGround`: ground-targeted spells (Earthquake) cast at the cursor on the key press
 *    instead of entering placement mode (the pre-placement behaviour). Default off (WoW style).
 *  - `castRing`: the thin GCD / cast progress ring at the hero's feet. Default on.
 * `gameplaySettings` is a tiny shared store: the settings panel writes it, the session reads it.
 */
import { el } from './dom';

export interface GameplaySettings {
  quickCastGround: boolean;
  castRing: boolean;
  /** +autoattack: swing timer bars above the player frame (main hand, off hand, Auto Shot). Default off. */
  swingTimer: boolean;
}

export const DEFAULT_GAMEPLAY: Readonly<GameplaySettings> = Object.freeze({ quickCastGround: false, castRing: true, swingTimer: false });
export const GAMEPLAY_KEY = 'keystone.gameplay.v1';

export function sanitizeGameplay(raw: unknown): GameplaySettings {
  const s: GameplaySettings = { ...DEFAULT_GAMEPLAY };
  if (!raw || typeof raw !== 'object') return s;
  const r = raw as Record<string, unknown>;
  if (typeof r['quickCastGround'] === 'boolean') s.quickCastGround = r['quickCastGround'];
  if (typeof r['castRing'] === 'boolean') s.castRing = r['castRing'];
  if (typeof r['swingTimer'] === 'boolean') s.swingTimer = r['swingTimer'];
  return s;
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadGameplay(storage: Pick<Storage, 'getItem'> | null = defaultStorage()): GameplaySettings {
  try {
    const raw = storage?.getItem(GAMEPLAY_KEY);
    return sanitizeGameplay(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_GAMEPLAY };
  }
}

export function saveGameplay(s: GameplaySettings, storage: Pick<Storage, 'setItem'> | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(GAMEPLAY_KEY, JSON.stringify(sanitizeGameplay(s)));
    return true;
  } catch {
    return false;
  }
}

class GameplayStore {
  private value: GameplaySettings | null = null;

  get(): Readonly<GameplaySettings> {
    return (this.value ??= loadGameplay());
  }

  set(patch: Partial<GameplaySettings>): void {
    this.value = sanitizeGameplay({ ...this.get(), ...patch });
    saveGameplay(this.value);
  }
}

export const gameplaySettings = new GameplayStore();

/** Adds a "Gameplay" section to the settings panel. Returns a sync function. */
export function mountGameplayControls(panel: HTMLElement, before: Node | null = null): () => void {
  const section = el('div', 'gp-settings');
  panel.insertBefore(section, before);
  el('div', 'gfx-title', section, 'Gameplay');
  const check = (label: string, key: keyof GameplaySettings, hint: string): HTMLInputElement => {
    const r = el('label', 'gfx-row check', section);
    el('span', '', r, label);
    r.title = hint;
    const input = el('input', '', r);
    input.type = 'checkbox';
    input.addEventListener('change', () => gameplaySettings.set({ [key]: input.checked }));
    return input;
  };
  const quick = check('Quick cast ground spells', 'quickCastGround', 'Ground spells (Earthquake) land at the cursor on the key press, without the placement circle.');
  const ring = check('Cast ring at your feet', 'castRing', 'A thin ring under your hero that fills during the global cooldown and casts.');
  const swing = check('Swing timer', 'swingTimer', 'Bars above your frame showing your next auto-attack (off hand and Auto Shot too).');
  return () => {
    const s = gameplaySettings.get();
    quick.checked = s.quickCastGround;
    ring.checked = s.castRing;
    swing.checked = s.swingTimer;
  };
}
